// Node-type registry passed to ReactFlow. Mirrors the pattern at
// components/workflows/nodes/index.ts — one keyed export per type so
// ReactFlow can dispatch by `node.type`.
//
// v0.4: dispatched kinds (turn_marker / llm_round / tool_call / peer)
// go through `withFocalDispatch(Compact, Rich)` so the focal turn
// flowers into a rich card. Until focalTurnId is non-null (Task 11
// wires it), the HOF always picks the compact variant → v0.3 visual
// behavior preserved with zero feature flag.
//
// Non-dispatched structural kinds (fan_collapsed_chip, phase_stack_
// group, collapsed_phase_stack) have no rich variant — they're
// layout primitives, not agent cards (architect A2').

import { byKind } from './byKind';
import { withFocalDispatch } from './withFocalDispatch';
import TurnMarkerNodeCompactImpl from './TurnMarkerNodeCompact';
import LlmRoundNodeCompactImpl from './LlmRoundNodeCompact';
import ToolCallDagNodeCompactImpl from './ToolCallDagNodeCompact';
import PeerNodeCompactImpl from './PeerNodeCompact';
import { TurnMarkerNodeRich } from './rich/TurnMarkerNodeRich';
import { LlmRoundNodeRich } from './rich/LlmRoundNodeRich';
import { ToolCallDagNodeRich } from './rich/ToolCallDagNodeRich';
import { PeerNodeRich } from './rich/PeerNodeRich';
import FanCollapsedChipImpl from './FanCollapsedChip';
import PhaseStackGroupNodeImpl from './PhaseStackGroupNode';
import CollapsedPhaseStackNodeImpl from './CollapsedPhaseStackNode';
import TurnSeparatorNodeImpl from './TurnSeparatorNode';

export const chatDagNodeTypes = {
  turn_marker: byKind(
    'turn_marker',
    withFocalDispatch(
      TurnMarkerNodeCompactImpl as any,
      TurnMarkerNodeRich,
    ) as any,
  ),
  llm_round: byKind(
    'llm_round',
    withFocalDispatch(LlmRoundNodeCompactImpl as any, LlmRoundNodeRich) as any,
  ),
  tool_call: byKind(
    'tool_call',
    withFocalDispatch(
      ToolCallDagNodeCompactImpl as any,
      ToolCallDagNodeRich,
    ) as any,
  ),
  peer: byKind(
    'peer',
    withFocalDispatch(PeerNodeCompactImpl as any, PeerNodeRich) as any,
  ),
  fan_collapsed_chip: byKind('fan_collapsed_chip', FanCollapsedChipImpl as any),
  phase_stack_group: byKind('phase_stack_group', PhaseStackGroupNodeImpl as any),
  collapsed_phase_stack: byKind(
    'collapsed_phase_stack',
    CollapsedPhaseStackNodeImpl as any,
  ),
  turn_separator: byKind('turn_separator', TurnSeparatorNodeImpl as any),
};

// Back-compat alias exports for any downstream consumer that was
// importing the compact bodies by their old "just the node" name.
export {
  TurnMarkerNodeCompactImpl as TurnMarkerNode,
  LlmRoundNodeCompactImpl as LlmRoundNode,
  ToolCallDagNodeCompactImpl as ToolCallDagNode,
  PeerNodeCompactImpl as PeerNode,
  FanCollapsedChipImpl as FanCollapsedChip,
  PhaseStackGroupNodeImpl as PhaseStackGroupNode,
  CollapsedPhaseStackNodeImpl as CollapsedPhaseStackNode,
  TurnSeparatorNodeImpl as TurnSeparatorNode,
};
